import { useEffect, useState } from "react";
import { AlarmClock, BarChart3, Bell, BellOff, BookOpen, Boxes, Building2, ChevronDown, ChevronLeft, ChevronRight, CircleDollarSign, ClipboardList, Crown, HelpCircle, KeyRound, Landmark, LayoutDashboard, LayoutGrid, LogOut, Menu, Package, Plus, ReceiptText, Send, Settings2, ShieldCheck, ShoppingCart, Users, WalletCards } from "lucide-react";
import { api } from "@/lib/api";
import Dashboard from "@/pages/Dashboard";
import AdminPanel from "@/pages/AdminPanel";
import WorkflowView from "@/pages/WorkflowView";
import Reports from "@/pages/Reports";
import Payments from "@/pages/Payments";
import CashAccounts from "@/pages/CashAccounts";
import Expenses from "@/pages/Expenses";
import Products from "@/pages/Products";
import Accounts from "@/pages/Accounts";
import Journals from "@/pages/Journals";
import Settings from "@/pages/Settings";
import Contacts from "@/pages/Contacts";
import ProductionOrders, { Boms } from "@/pages/Manufacturing";
import Warehouses from "@/pages/Warehouses";
import StockHistory from "@/pages/StockHistory";
import Assets from "@/pages/Assets";
import Subscription from "@/pages/Subscription";
import { ChangePassword } from "@/components/ChangePassword";

const menus = [
  { label: "Dashboard", icon: LayoutDashboard, module: "dashboard" },
  { label: "Penjualan", icon: ShoppingCart, items: ["Invoice Penjualan"], module: "penjualan" },
  { label: "Pembelian", icon: ClipboardList, items: ["Tagihan Pembelian"], module: "pembelian" },
  { label: "Kas & Bank", icon: WalletCards, items: ["Rekening", "Penerimaan Uang", "Pengeluaran Uang"], module: "kas" },
  { label: "Biaya", icon: ReceiptText, module: "biaya" },
  { label: "Produk", icon: Package, items: ["Daftar Produk", "Gudang", "Riwayat Stok"], module: "produk" },
  { label: "Produksi", icon: Boxes, items: ["Bill of Materials", "Perintah Produksi"], module: "produksi" },
  { label: "Akuntansi", icon: BookOpen, items: ["Daftar Akun", "Jurnal Umum"], module: "akuntansi" },
  { label: "Aset", icon: Landmark, items: ["Aset Tetap"], module: "akuntansi" },
  { label: "Laporan", icon: BarChart3, module: "laporan" },
  { label: "Kontak", icon: Users, module: "kontak" },
  { label: "Pengaturan", icon: Settings2, module: "pengaturan" },
];

const CREATE_ITEMS = [
  { label: "Invoice Penjualan", desc: "Tagih pelanggan Anda", icon: ShoppingCart, view: "Invoice Penjualan", module: "penjualan", create: true },
  { label: "Tagihan Pembelian", desc: "Catat tagihan dari supplier", icon: ClipboardList, view: "Tagihan Pembelian", module: "pembelian", create: true },
  { label: "Terima Pembayaran", desc: "Catat uang masuk", icon: CircleDollarSign, view: "Penerimaan Uang", module: "kas" },
  { label: "Kirim Pembayaran", desc: "Catat uang keluar", icon: Send, view: "Pengeluaran Uang", module: "kas" },
  { label: "Biaya", desc: "Catat pengeluaran operasional", icon: ReceiptText, view: "Biaya", module: "biaya" },
  { label: "Jurnal Umum", desc: "Buat jurnal penyesuaian", icon: BookOpen, view: "Jurnal Umum", module: "akuntansi" },
  { label: "Kontak Baru", desc: "Pelanggan, supplier, karyawan", icon: Users, view: "Kontak", module: "kontak" },
];

const SEVERITY = { danger: "sev-danger", warning: "sev-warning", info: "sev-info", success: "sev-success" };

export default function AppShell({ user, onLogout }) {
  const [collapsed, setCollapsed] = useState(false);
  const [open, setOpen] = useState("Dashboard");
  const [dashboard, setDashboard] = useState(null);
  const [view, setView] = useState("Dashboard");
  const [menuOpen, setMenuOpen] = useState(null);
  const [notifs, setNotifs] = useState({ alerts: [], count: 0 });
  const [notifSeen, setNotifSeen] = useState(false);
  const [autoCreate, setAutoCreate] = useState(false);
  const [pwModal, setPwModal] = useState(false);
  const [sub, setSub] = useState(null);
  useEffect(() => { api("/dashboard").then(setDashboard).catch(console.error); }, []);
  useEffect(() => { api("/subscription").then(setSub).catch(() => {}); }, [view]);
  useEffect(() => { api("/notifications").then(setNotifs).catch(() => {}); }, [view]);
  useEffect(() => {
    const esc = (e) => e.key === "Escape" && setMenuOpen(null);
    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, []);
  const allowed = user?.modules;
  const can = (m) => !allowed || allowed.includes("*") || allowed.includes(m);
  const visibleMenus = menus.filter((m) => can(m.module));
  const permittedViews = new Set(visibleMenus.flatMap((m) => [m.label, ...(m.items || [])]));
  permittedViews.add("Paket & Langganan");
  if (user?.is_superadmin) permittedViews.add("Admin Panel");
  const isAdminPanel = view === "Admin Panel";
  const subLocked = Boolean(sub?.locked) && !user?.is_superadmin;
  const showTrialChip = sub && sub.days_left !== null && (sub.is_trial || sub.days_left <= 14);
  const k = dashboard?.kpis || {};
  const denied = !permittedViews.has(view);
  const go = (v, create = false) => {
    setView(v);
    setAutoCreate(create);
    setMenuOpen(null);
    const parent = menus.find((m) => m.items?.includes(v));
    setOpen(parent ? parent.label : v);
  };
  const badge = !notifSeen && notifs.count > 0 ? notifs.count : 0;
  return (
    <div className={`app-shell ${collapsed ? "sidebar-collapsed" : ""}`}>
      <aside className="sidebar">
        <div className="side-brand">
          <div className="brand-mark small">K<span>•</span></div>
          {!collapsed && <span className="brand-name">Kiyana IAC</span>}
          <button data-testid="sidebar-collapse-button" className="icon-btn collapse" onClick={() => setCollapsed(!collapsed)}>{collapsed ? <ChevronRight size={17} /> : <ChevronLeft size={17} />}</button>
        </div>
        <div className="workspace-mini" data-testid="workspace-selector">
          <Building2 size={16} />
          {!collapsed && <><span>{dashboard?.company?.name || "Workspace"}</span><ChevronDown size={14} /></>}
        </div>
        <nav>
          {isAdminPanel ? (
            <div className="admin-mode-note" data-testid="admin-mode-note">
              <ShieldCheck size={17} />
              {!collapsed && <><b>Mode Admin Platform</b><small>Navigasi modul dinonaktifkan. Klik "App Panel" untuk kembali ke aplikasi.</small></>}
            </div>
          ) : subLocked ? (
            <div className="admin-mode-note" data-testid="locked-mode-note">
              <AlarmClock size={17} />
              {!collapsed && <><b>Masa aktif berakhir</b><small>Perbarui paket Anda untuk membuka kembali seluruh modul.</small></>}
            </div>
          ) : visibleMenus.map(({ label, icon: Icon, items }) => {
            const single = items && items.length === 1;
            return (
            <div key={label}>
              <button data-testid={`nav-${label.toLowerCase().replaceAll(" ", "-")}`} className={`nav-item ${open === label ? "active" : ""}`} onClick={() => { if (single) { go(items[0]); return; } setOpen(open === label && items ? "" : label); if (!items) go(label); }} title={collapsed ? label : ""}>
                <Icon size={18} />
                {!collapsed && <span>{label}</span>}
                {!collapsed && items && !single && <ChevronDown size={14} className="nav-chevron" />}
              </button>
              {!collapsed && items && !single && open === label && (
                <div className="subnav">{items.map((i) => <button data-testid={`subnav-${i.toLowerCase().replaceAll(" ", "-")}`} key={i} className={view === i ? "active" : ""} onClick={() => go(i)}>{i}</button>)}</div>
              )}
            </div>
            );
          })}
        </nav>
        <div className="sidebar-bottom">
          <button data-testid="sidebar-help-button" className="nav-item"><HelpCircle size={18} />{!collapsed && <span>Pusat Bantuan</span>}</button>
          <button data-testid="sidebar-logout-button" className="nav-item logout" onClick={onLogout}><LogOut size={18} />{!collapsed && <span>Keluar</span>}</button>
        </div>
      </aside>
      <main className="main-content">
        <header className="topbar">
          <button data-testid="mobile-menu-button" className="icon-btn mobile-only"><Menu size={19} /></button>
          <div className="topbar-crumb" data-testid="topbar-crumb">
            <small>{dashboard?.company?.name || "Workspace"}</small>
            <strong>{denied ? "Akses ditolak" : subLocked ? "Paket & Masa Aktif" : view}</strong>
          </div>
          <div className="top-actions">
            {showTrialChip && (
              <button data-testid="trial-countdown-chip" className={`trial-chip ${sub.days_left <= 2 ? "urgent" : ""}`} onClick={() => go("Paket & Langganan")}>
                <AlarmClock size={13} /> {sub.is_trial ? "Trial" : sub.plan}: {sub.days_left > 0 ? `${sub.days_left} hari lagi` : "berakhir"}
              </button>
            )}
            {user?.is_superadmin && (
              <button data-testid="panel-switch-button" className={`panel-switch ${isAdminPanel ? "admin" : ""}`} onClick={() => go(isAdminPanel ? "Dashboard" : "Admin Panel")}>
                {isAdminPanel ? <><LayoutGrid size={14} /> App Panel</> : <><ShieldCheck size={14} /> Admin Panel</>}
              </button>
            )}
            <div className="top-menu-wrap">
              {!subLocked && <button data-testid="quick-create-button" className={`quick-btn ${menuOpen === "create" ? "open" : ""}`} onClick={() => setMenuOpen(menuOpen === "create" ? null : "create")}><Plus size={16} /> Buat Baru <ChevronDown size={14} /></button>}
              {menuOpen === "create" && (
                <div className="top-menu create-menu" data-testid="quick-create-menu">
                  <p className="top-menu-title">Buat transaksi baru</p>
                  {CREATE_ITEMS.filter((c) => can(c.module)).map((c) => (
                    <button key={c.label} data-testid={`create-${c.view.toLowerCase().replaceAll(" ", "-")}`} className="top-menu-item" onClick={() => go(c.view, Boolean(c.create))}>
                      <span className="tmi-icon"><c.icon size={15} /></span>
                      <span className="tmi-body"><b>{c.label}</b><small>{c.desc}</small></span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="top-menu-wrap">
              <button data-testid="notifications-button" className={`icon-btn notification ${menuOpen === "notif" ? "open" : ""}`} onClick={() => { const opening = menuOpen !== "notif"; setMenuOpen(opening ? "notif" : null); setNotifSeen(true); if (opening) api("/notifications/read", { method: "POST" }).catch(() => {}); }}>
                <Bell size={18} />
                {badge > 0 && <span className="notif-badge" data-testid="notification-badge">{badge > 9 ? "9+" : badge}</span>}
              </button>
              {menuOpen === "notif" && (
                <div className="top-menu notif-menu" data-testid="notification-menu">
                  <div className="top-menu-head">
                    <p className="top-menu-title">Notifikasi</p>
                    {notifs.count > 0 && <span className="notif-count-chip">{notifs.count}</span>}
                  </div>
                  {notifs.alerts.length === 0 ? (
                    <div className="notif-empty" data-testid="notification-empty"><BellOff size={19} /><b>Tidak ada notifikasi</b><small>Semua invoice, tagihan, dan stok dalam kondisi aman.</small></div>
                  ) : notifs.alerts.map((a, i) => (
                    <button key={a.id} data-testid={`notification-item-${i}`} className="top-menu-item notif-item" onClick={() => go(a.view)}>
                      <span className={`notif-dot ${SEVERITY[a.severity] || "sev-info"}`} />
                      <span className="tmi-body"><b>{a.title}</b><small>{a.message}</small></span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="top-menu-wrap">
              <button className={`user-chip ${menuOpen === "user" ? "open" : ""}`} data-testid="user-profile" onClick={() => setMenuOpen(menuOpen === "user" ? null : "user")}>
                <div className="avatar">{user.name?.slice(0, 2).toUpperCase()}</div>
                <div className="user-chip-text"><strong>{user.name}</strong><small>{user.role === "Owner" ? "Owner" : (user.roles || []).join(", ") || "Member"}</small></div>
                <ChevronDown size={14} />
              </button>
              {menuOpen === "user" && (
                <div className="top-menu user-menu" data-testid="user-menu">
                  <div className="user-menu-head">
                    <div className="avatar big">{user.name?.slice(0, 2).toUpperCase()}</div>
                    <div><b>{user.name}</b><small>{user.email}</small><span className="role-chip">{user.role === "Owner" ? "Pemilik" : (user.roles || []).join(", ") || "Member"}</span></div>
                  </div>
                  {can("pengaturan") && <button data-testid="user-menu-settings" className="top-menu-item slim" onClick={() => go("Pengaturan")}><Settings2 size={15} /> Pengaturan</button>}
                  <button data-testid="user-menu-subscription" className="top-menu-item slim" onClick={() => go("Paket & Langganan")}><Crown size={15} /> Paket & Langganan</button>
                  <button data-testid="user-menu-password" className="top-menu-item slim" onClick={() => { setMenuOpen(null); setPwModal(true); }}><KeyRound size={15} /> Ganti kata sandi</button>
                  <button data-testid="user-menu-logout" className="top-menu-item slim danger" onClick={onLogout}><LogOut size={15} /> Keluar</button>
                </div>
              )}
            </div>
          </div>
        </header>
        {menuOpen && <div className="menu-backdrop" onClick={() => setMenuOpen(null)} />}
        <div className="page-wrap">
          {subLocked ? <Subscription locked />
          : denied ? (
            <div className="surface access-denied" data-testid="access-denied">
              <h2>Akses ditolak</h2>
              <p className="muted">Anda tidak memiliki akses ke modul ini. Hubungi pemilik atau administrator perusahaan untuk mendapatkan akses.</p>
              <button className="primary-btn" onClick={() => go("Dashboard")}>Kembali ke Dashboard</button>
            </div>
          ) : view === "Admin Panel" ? <AdminPanel />
            : view === "Paket & Langganan" ? <Subscription locked={false} />
            : view === "Dashboard" ? <Dashboard data={dashboard} k={k} user={user} onNavigate={go} can={can} />
            : view === "Laporan" ? <Reports />
            : view === "Buku Besar" ? <Reports key="gl" initialTab="gl" />
            : view === "Penerimaan Uang" ? <Payments key="rcv" mode="RECEIVE" />
            : view === "Pengeluaran Uang" ? <Payments key="pay" mode="PAY" />
            : view === "Rekening" ? <CashAccounts />
            : view === "Biaya" ? <Expenses />
            : view === "Daftar Produk" ? <Products />
            : view === "Daftar Akun" ? <Accounts />
            : view === "Jurnal Umum" ? <Journals />
            : view === "Pengaturan" ? <Settings />
            : view === "Kontak" ? <Contacts />
            : view === "Bill of Materials" ? <Boms />
            : view === "Perintah Produksi" ? <ProductionOrders />
            : view === "Gudang" ? <Warehouses />
            : view === "Riwayat Stok" ? <StockHistory />
            : view === "Aset Tetap" ? <Assets />
            : <WorkflowView view={view} autoCreate={autoCreate} onCreateConsumed={() => setAutoCreate(false)} />}
        </div>
      </main>
      {pwModal && <ChangePassword forced={false} onClose={() => setPwModal(false)} onDone={() => setPwModal(false)} />}
    </div>
  );
}
